import React from 'react';
import { Alert, CircularProgress, type ModalProps } from '@mui/material';
import Box from '@mui/material/Box';
import { useDispatch } from 'react-redux';

import { useMarkInvoiceAsVoidedMutation } from 'generated/graphql';
import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { showMessageAction } from 'containers/Notification/actions';

export const MarkInvoiceAsVoidedModal = ({
  invoiceId,
  style,
  onClickClose = (): void => {},
  ...props
}: {
  invoiceId: string;
  onClickClose: () => void;
} & Omit<ModalProps, 'children'>): JSX.Element => {
  const dispatch = useDispatch();
  const [
    markInvoiceAsVoidedMutation,
    { loading: markInvoiceAsVoidedMutationLoading, error: markInvoiceAsVoidedMutationError },
  ] = useMarkInvoiceAsVoidedMutation({
    refetchQueries: ['givingAccountSummary', 'givingAccountTransactions', 'invoices'],
    onCompleted: () => {
      dispatch(showMessageAction('Invoice marked as voided successfully.'));
    },
  });

  const handleSubmit = async () => {
    await markInvoiceAsVoidedMutation({
      variables: {
        invoiceId,
      },
    });
    onClickClose();
  };

  return (
    <Modal
      disableAutoFocus
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
      {...props}
    >
      <ModalContents
        onClickClose={onClickClose}
        body={
          <>
            <ModalTitle>Mark Invoice as Voided</ModalTitle>
            <Box paddingTop={4}>
              Are you sure you want to mark the invoice as voided? This action cannot be undone.
            </Box>
            {markInvoiceAsVoidedMutationError && (
              <Alert severity="error">
                Failed to mark the invoice as voided:
                <br />
                <br />
                {markInvoiceAsVoidedMutationError?.message}
              </Alert>
            )}
          </>
        }
        actions={
          <>
            <ModalButton
              variant="outlined"
              disabled={markInvoiceAsVoidedMutationLoading}
              onClick={(): void => {
                onClickClose();
              }}
            >
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton disabled={markInvoiceAsVoidedMutationLoading} onClick={handleSubmit}>
              {markInvoiceAsVoidedMutationLoading ? (
                <CircularProgress size={21} />
              ) : (
                'Mark as Voided'
              )}
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};
